import { PlayerHookType } from '@cbsinteractive/avia-js';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';
export class AviaPlayerPreferredVideoCodecHook extends AbstractAviaPlayerHook {
    hookType = PlayerHookType.RESPONSE;
    constructor(argument) {
        super({
            ...argument,
            name: 'AviaPlayerPreferredVideoCodecHook'
        });
        this.tagEventsHandler(this.#onTagEvent, [
            TagEvent.ON_CREATE_PLAYER_SUCCESS
        ]);
    }
    #onTagEvent = (event) => {
        switch (event?.type) {
            case TagEvent.ON_CREATE_PLAYER_SUCCESS:
                this.removeTagEventsHandler(this.#onTagEvent, this.tag.eventDispatcher, [TagEvent.ON_CREATE_PLAYER_SUCCESS]).then((data) => {
                    if (window?.SmartTag?.deviceCapabilities) {
                        this.setPreferredVideoCodecs(window.SmartTag.deviceCapabilities);
                    }
                    else {
                        this.tag.controller.playerManager?.playerAdapter?.getDeviceCapabilities?.()?.then((capabilities) => {
                            this.setPreferredVideoCodecs(capabilities);
                        });
                    }
                });
                break;
        }
    };
    #debugCapabilities = (capabilities) => {
        const debugCapabilitiesOverride = this?.tag?.model?.config?.tag?.Hooks?.params?.PreferredVideoCodecHook?.debug?.capabilities;
        if (!this.utils.Type.isNullOrUndefined(debugCapabilitiesOverride) && !this.utils.Type.isEmpty(debugCapabilitiesOverride)) {
            this?.logger?.log('overriding capabilities', 'debugCapabilitiesOverride', debugCapabilitiesOverride);
            capabilities = {
                ...capabilities,
                ...debugCapabilitiesOverride
            };
        }
        return capabilities;
    };
    setPreferredVideoCodecs = (capabilities) => {
        this?.logger?.log('setPreferredVideoCodecs', 'capabilities', capabilities);
        capabilities = this.#debugCapabilities(capabilities);
        if (capabilities?.supportsUhd && !capabilities?.supportsDolbyVision && !capabilities?.supportsHdr10) {
            this.registerHook();
            return;
        }
        const preferredVideoCodecs = [];
        if (capabilities?.supportsDolbyVision) {
            this?.logger?.log('This TV supports Dolby Vision. Adding dvhe codec.');
            preferredVideoCodecs.push('dvhe');
        }
        if (capabilities?.supportsHdr10) {
            this?.logger?.log('This TV supports hdr10. Adding hvc and hev codecs.');
            preferredVideoCodecs.push('hev1.2');
            preferredVideoCodecs.push('hvc1.2');
            preferredVideoCodecs.push('hev1.1');
            preferredVideoCodecs.push('hvc1.1');
        }
        this?.logger?.log('Adding avc codec.');
        preferredVideoCodecs.push('avc');
        this.params.CONTENT.resource = {
            ...this.params?.CONTENT?.resource,
            playback: {
                ...this.params?.CONTENT?.resource?.playback,
                preferredVideoCodecs
            }
        };
    };
    callback = (context) => {
        if (context.value.url.indexOf('.mpd') === -1) {
            return;
        }
        const response = context?.value;
        if (this.utils.Type.isNullOrUndefined(response)) {
            this?.logger?.warn('there was not a response in the manifest', response?.url);
            return;
        }
        this.#useManifestToConfigurePreferredVideoCodecs(response?.data);
    };
    #useManifestToConfigurePreferredVideoCodecs = (manifestContents) => {
        if (this.utils.Type.isNullOrUndefined(manifestContents)) {
            this?.logger?.warn('manifest contents are empty', manifestContents);
            return;
        }
        const manifestCodecs = this.#getManifestVideoCodecs(manifestContents);
        const SDR_4K_CODEC_HVC = 'hvc1.1';
        const SDR_4K_CODEC_HEV = 'hev1.1';
        const HDR_4K_CODEC_HVC = 'hvc1.2';
        const HDR_4K_CODEC_HEV = 'hev1.2';
        if (manifestCodecs.indexOf(SDR_4K_CODEC_HVC) !== -1 || manifestCodecs.indexOf(SDR_4K_CODEC_HEV) !== -1) {
            this?.logger?.log('This manifest has hvc1.1. Setting codec to HVC =>');
            this.#configureShakaPlayer(['hev1.1', 'hvc1.1', 'avc']);
        }
        else if (manifestCodecs.indexOf(HDR_4K_CODEC_HVC) !== -1 || manifestCodecs.indexOf(HDR_4K_CODEC_HEV) !== -1) {
            this?.logger?.log('This manifest has hvc1.2. Setting codec to AVC =>');
            this.#configureShakaPlayer(['avc']);
        }
        else {
            this?.logger?.log('no hvc content in manifest, setting default preferred codecs');
            this.#configureShakaPlayer(['avc']);
        }
    };
    #getManifestVideoCodecs(manifestContents) {
        const videoCodecs = {};
        try {
            const parser = new DOMParser();
            const xml = parser.parseFromString(manifestContents, 'text/xml');
            const representations = xml.querySelectorAll('Representation');
            representations.forEach((representation) => {
                const codecs = representation.getAttribute('codecs');
                if (this.utils.Type.isNullOrUndefined(codecs)) {
                    return;
                }
                const irrelevant = ['ec-3', 'mp4a', 'wvtt'];
                const hasIrrelevant = irrelevant.some((codec) => {
                    return codecs.indexOf(codec) !== -1;
                });
                if (hasIrrelevant) {
                    return;
                }
                this?.logger?.log('relevant codecs in representations found =>', codecs);
                videoCodecs[codecs] = true;
            });
        }
        catch (error) {
            this?.logger?.log('there was an error parsing manifest =>', error);
        }
        const uniqueVideoCodecs = Object.keys(videoCodecs);
        this?.logger?.log('#getManifestVideoCodecs uniqueVideoCodecs', uniqueVideoCodecs);
        return uniqueVideoCodecs;
    }
    #configureShakaPlayer(codecsPayload) {
        const shakaAdapter = this.tag?.controller?.playerManager?.player?.adaptedPlayer?.getAdapter('playback');
        if (!shakaAdapter) {
            this?.logger?.log('unable to retrieve playback adapter');
            return;
        }
        shakaAdapter?.player?.configure({
            preferredVideoCodecs: codecsPayload
        });
        shakaAdapter.playerConfig = {
            ...shakaAdapter?.playerConfig,
            preferredVideoCodecs: codecsPayload
        };
    }
}
